/**
 * Stats Dashboard Component
 * Displays clickable statistics tiles
 */

import React from 'react';
import { AlertTriangle, TrendingUp, CheckCircle, XCircle } from 'lucide-react';
import { AlertStats } from '../types';

interface StatsDashboardProps {
  stats: AlertStats | null;
  onStatClick?: (filterType: string, filterValue: string) => void;
}

export const StatsDashboard: React.FC<StatsDashboardProps> = ({ stats, onStatClick }) => {
  if (!stats) return null;

  const handleStatClick = (filterType: string, filterValue: string) => {
    if (onStatClick) {
      onStatClick(filterType, filterValue);
    }
  };

  return (
    <div className="stats-dashboard">
      <div className="flex-between">
        <div className="flex gap-sm">
          <TrendingUp size={14} className="text-warning" />
          <h3 className="stats-title">Statistics Overview</h3>
        </div>
      </div>

      <div className="stats-grid">
        <div 
          className="stat-item stat-clickable" 
          onClick={() => handleStatClick('priority', 'high')}
        >
          <div className="stat-icon stat-icon-high">
            <AlertTriangle />
          </div>
          <div className="stat-value stat-high">{stats.high_priority || 0}</div>
          <div className="stat-label">High</div>
        </div>

        <div 
          className="stat-item stat-clickable" 
          onClick={() => handleStatClick('priority', 'medium')}
        >
          <div className="stat-icon stat-icon-medium">
            <AlertTriangle />
          </div>
          <div className="stat-value stat-medium">{stats.medium_priority || 0}</div>
          <div className="stat-label">Medium</div>
        </div>

        <div 
          className="stat-item stat-clickable" 
          onClick={() => handleStatClick('priority', 'low')}
        >
          <div className="stat-icon stat-icon-low">
            <AlertTriangle />
          </div>
          <div className="stat-value stat-low">{stats.low_priority || 0}</div>
          <div className="stat-label">Low</div>
        </div>

        <div className="stat-item">
          <div className="stat-icon stat-icon-total">
            <AlertTriangle />
          </div>
          <div className="stat-value">{stats.total_alerts || 0}</div>
          <div className="stat-label">Total</div>
        </div>

        <div 
          className="stat-item stat-clickable" 
          onClick={() => handleStatClick('rectification', 'rectified')}
        >
          <div className="stat-icon stat-icon-success">
            <CheckCircle />
          </div>
          <div className="stat-value stat-success">{stats.rectified_count || 0}</div>
          <div className="stat-label">Rectified</div>
        </div>

        <div 
          className="stat-item stat-clickable" 
          onClick={() => handleStatClick('rectification', 'not rectified')}
        >
          <div className="stat-icon stat-icon-error">
            <XCircle />
          </div>
          <div className="stat-value stat-error">{stats.not_rectified_count || 0}</div>
          <div className="stat-label">Pending</div>
        </div>

        <div 
          className="stat-item stat-clickable" 
          onClick={() => handleStatClick('accuracy', 'true')}
        >
          <div className="stat-icon stat-icon-success">
            <CheckCircle />
          </div>
          <div className="stat-value stat-success">{stats.accurate_alerts || 0}</div>
          <div className="stat-label">Accurate</div>
        </div>

        <div 
          className="stat-item stat-clickable" 
          onClick={() => handleStatClick('accuracy', 'false')}
        >
          <div className="stat-icon stat-icon-error">
            <XCircle />
          </div>
          <div className="stat-value stat-error">{stats.inaccurate_alerts || 0}</div>
          <div className="stat-label">Inaccurate</div>
        </div>
      </div>
    </div>
  );
};
